import NotesIcon from './NotesIcon';
import IdeaReferencePicker from './IdeaReferencePicker';
import DiscussionIcon from './DiscussionIcon';
import { getIdeaFontFamily, getIdeaFontSize } from '../../config/nodeFonts';
import { useState, useRef, useEffect, forwardRef, useImperativeHandle, useCallback, useMemo } from 'react';
import { FaSpotify } from 'react-icons/fa';
import NodeContent from './NodeContent';
import {
  getThemeCanvasSolidColor,
  getThemeNodeColor,
  getThemeNodeStyle,
} from '../../config/themes';
import '../../styles/components/Node.css';
import {
  getLinkProvider,
  getLinkProviderLabel,
} from '../../utils/mindmap/linkProviders';
import { fetchNodeAssetContent } from '../../utils/mindmap/apiClient';
import { getSemanticZoomLabel } from '../../utils/mindmap/semanticZoom';

const urlOnlyRegex = /^https?:\/\/[^\s]+$/i;
export const NODE_LONG_PRESS_DELAY_MS = 550;
export const NODE_COMPACT_MAX_WIDTH = 238;
export const NODE_EXPANDED_MAX_WIDTH = 357;
export const URL_NODE_COMPACT_MAX_WIDTH = 184;
export const NODE_HORIZONTAL_PADDING = 8;
export const NODE_MINIMUM_TEXT_SAMPLE = 'MMMM';
export const getIdeaDomId = (ideaId) => `mindmap-idea-${encodeURIComponent(String(ideaId))}`;
const NODE_LONG_PRESS_MOVE_TOLERANCE = 10;
const NODE_MIN_WIDTH_FALLBACK = 72;
const DESKTOP_DRAG_THRESHOLD_PX = 5;

const responsibilityInitials = (displayName) => String(displayName || 'Collaborator')
  .trim()
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part[0]?.toUpperCase())
  .join('') || '?';

const getUrlIfUrlOnly = (text, explicitUrl) => {
  if (explicitUrl) return explicitUrl;
  if (typeof text !== 'string') return null;
  const trimmed = text.trim();
  if (!trimmed) return null;
  return urlOnlyRegex.test(trimmed) ? trimmed : null;
};

const isMindmappiMapUrl = (rawUrl) => {
  if (!rawUrl) return false;
  try {
    const u = new URL(rawUrl);
    const hasMapParam = !!u.searchParams.get('map');
    if (!hasMapParam) return false;

    const host = u.hostname.toLowerCase().replace(/^www\./, '');
    const currentHost =
      typeof window !== 'undefined' && window.location && window.location.hostname
        ? window.location.hostname.toLowerCase().replace(/^www\./, '')
        : null;

    if (host === 'localhost' || host === '127.0.0.1') return true;
    if (currentHost && host === currentHost) return true;
    if (host === 'mindmappi.com' || host.endsWith('.mindmappi.com')) return true;

    return false;
  } catch {
    return false;
  }
};

export const getMindmappiIdeaTarget = (rawUrl, currentMindmapId = null) => {
  if (!rawUrl) return null;
  try {
    const base = typeof window !== 'undefined' ? window.location.origin : 'https://mindmappi.com';
    const url = new URL(rawUrl, base);
    if (!isMindmappiMapUrl(url.toString()) && !(url.origin === base && !currentMindmapId)) return null;
    const mapId = url.searchParams.get('map');
    const nodeId = url.searchParams.get('node');
    return mapId === currentMindmapId && nodeId ? nodeId : null;
  } catch {
    return null;
  }
};

const Node = forwardRef(({
  initialText = '',
  onTextChange,
  color = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  theme = null,
  isDarkMode = false,
  x, y,
  onPositionChange,
  isSelected = false,
  onSelect,
  onSelectionGlide,
  id,
  animate = false,
  opacity = 1,
  isRoot = false,
  fontSize = null,
  fontFamily = null,
  onDragComplete,
  editComplete,
  onDragStart,
  onDragMove,
  onDragEnd,
  isHighlighted = false,
  collapsedChildren = null,
  onExpandNode,
  onCollapseNode,
  hasChildren = false,
  getChildrenPositions = null,
  getParentNodeData = null,
  onSizeChange = null,
  onEditStart = null,
  onOpenPreview,
  onNavigateIdeaReference,
  referenceCandidates = [],
  onChooseIdeaReference,
  onHighlightIdeaReference,
  linkUrl,
  linkMeta,
  attachment = null,
  mindmapId = null,
  onOpenAttachment = null,
  onDownloadAttachment = null,
  onContextMenu,
  onClearLinkMeta,
  isTask = false,
  taskDone = false,
  responsiblePrincipal = null,
  onToggleTaskDone,
  emoji = null,
  embeddedMindmapId = null,
  isEmbeddedMindmapLink = false,
  onMapLinkTitleCommit = null,
  userId = null,
  creatorName = null,
  currentUserId = null,
  canEdit = true,
  canCompleteTask = canEdit,
  canMove = canEdit,
  moderationNotice = null,
  isContextOnly = false,
  discussionSummary = null,
  onOpenDiscussion = null,
  info = null,
  onOpenInfo = null,
  onDisallowedEdit = null,
  lockPositionToState = false,
  screenToCanvasPosition = null,
  isPositionAnimating = false,
  semanticZoom = false,
  semanticZoomSize = null,
}, ref) => {
  const [text, setText] = useState(initialText);
  const [localPosition, setLocalPosition] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const isEditingRef = useRef(false);
  const [showCollapseButton, setShowCollapseButton] = useState(false);

  const showCollapseButtonTimeout = useRef(null);
  const previewTimeoutRef = useRef(null);
  const mapLinkTimeoutRef = useRef(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [attachmentThumbnailUrl, setAttachmentThumbnailUrl] = useState(null);
  const [shouldLoadAttachment, setShouldLoadAttachment] = useState(false);

  const divRef = useRef(null);
  const inputRef = useRef(null);
  const baseWidthRef = useRef(0);
  const mouseDownPos = useRef(null);
  const mouseDragActivatedRef = useRef(false);
  const suppressClickUntilRef = useRef(0);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const suppressMouseUntil = useRef(0);

  useEffect(() => {
    if (semanticZoom) {
      setShouldLoadAttachment(false);
      return undefined;
    }
    if (attachment?.kind !== 'image') {
      setShouldLoadAttachment(false);
      return undefined;
    }
    if (typeof IntersectionObserver === 'undefined' || !divRef.current) {
      setShouldLoadAttachment(true);
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setShouldLoadAttachment(true);
        observer.disconnect();
      }
    }, { rootMargin: '240px' });
    observer.observe(divRef.current);
    return () => observer.disconnect();
  }, [attachment?.assetId, attachment?.kind, semanticZoom]);

  useEffect(() => {
    if (
      attachment?.kind !== 'image'
      || !attachment.assetId
      || !mindmapId
      || !shouldLoadAttachment
      || semanticZoom
    ) {
      setAttachmentThumbnailUrl(null);
      return undefined;
    }
    let active = true;
    let objectUrl = null;
    fetchNodeAssetContent(mindmapId, attachment.assetId, 'thumbnail')
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setAttachmentThumbnailUrl(objectUrl);
      })
      .catch(() => {
        if (active) setAttachmentThumbnailUrl(null);
      });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment?.assetId, attachment?.kind, mindmapId, semanticZoom, shouldLoadAttachment]);

  const getEditorInitials = useCallback((editorId) => {
    if (!editorId) return '';
    const str = String(editorId).trim();
    if (!str) return '';
    const parts = str.split(/[^\p{L}\p{N}]+/u).filter(Boolean);
    if (parts.length === 0) {
      return str.slice(0, 2).toUpperCase();
    }
    const first = parts[0][0] || '';
    const second = parts.length > 1 ? (parts[1][0] || '') : '';
    return (first + second).toUpperCase();
  }, []);

  const beginEditing = useCallback(() => {
    if (!canEdit) {
      if (typeof onDisallowedEdit === 'function') {
        onDisallowedEdit();
      }
      return;
    }

    onEditStart?.();
    setReferenceDismissedText(null);
    setReferenceIndex(0);
    isEditingRef.current = true;
    setIsEditing(true);
    if (inputRef.current) {
      // Focus and select all text so the next key press replaces it
      inputRef.current.focus({ preventScroll: true });
      inputRef.current.setSelectionRange(0, inputRef.current.value.length);
    }
  }, [canEdit, onDisallowedEdit, onEditStart]);

  // Theme-controlled node font. Text measurement below must use the same
  // font as rendering or auto-sizing clips/overshoots the bubbles.
  const nodeFontFamily = getIdeaFontFamily(fontFamily, theme);
  const nodeFontSize = getIdeaFontSize(fontSize);
  const compactFontSize = fontSize == null ? 12 : nodeFontSize;
  const measuredTypographyRef = useRef({ family: nodeFontFamily, size: nodeFontSize });
  const isRichCardSizing = !semanticZoom && (
    Boolean(attachment) || Boolean(getUrlIfUrlOnly(text, linkUrl))
  );
  const configuredHorizontalPadding = Number(theme?.node?.horizontalPadding);
  const nodeHorizontalPadding = !isRichCardSizing
    && Number.isFinite(configuredHorizontalPadding)
    && configuredHorizontalPadding >= 0
    ? configuredHorizontalPadding
    : NODE_HORIZONTAL_PADDING;
  const nodeMinimumTextSample = !isRichCardSizing
    && typeof theme?.node?.minimumTextSample === 'string'
    && theme.node.minimumTextSample
    ? theme.node.minimumTextSample
    : NODE_MINIMUM_TEXT_SAMPLE;

  // Ordinary nodes use content-box sizing: CSS adds the inset once.
  // Rich cards retain their existing measurement and padding behavior.
  const measurementPadding = isRichCardSizing ? nodeHorizontalPadding : 0;

  // Define updateOvalSize first, before any useEffects that might call it
  const updateOvalSize = useCallback((element) => {
    if (!element) return;

    if (semanticZoom) {
      const width = Number.isFinite(semanticZoomSize?.width)
        ? semanticZoomSize.width
        : NODE_MIN_WIDTH_FALLBACK;
      const height = Number.isFinite(semanticZoomSize?.height)
        ? semanticZoomSize.height
        : 34;
      element.style.width = `${width}px`;
      element.style.minWidth = `${width}px`;
      element.style.maxWidth = `${width}px`;
      element.style.height = `${height}px`;
      element.style.minHeight = `${height}px`;
      element.style.fontSize = `${compactFontSize}px`;
      element.style.whiteSpace = 'nowrap';
      return;
    }

    if (attachment?.kind === 'image') {
      element.style.width = 'auto';
      element.style.minWidth = '0px';
      element.style.fontSize = `${nodeFontSize}px`;
      element.style.whiteSpace = 'normal';
      element.style.height = 'auto';
      return;
    }

    // The textarea is absolutely positioned while editing, so it does not
    // contribute text to the node's intrinsic size. Measure the raw editor
    // value directly instead of allowing the bubble to collapse.
    const renderedText = isEditing ? text : (element.textContent || '');
    const isCompactUrlNode = !isSelected && !!getUrlIfUrlOnly(text, linkUrl);

    if (isCompactUrlNode) {
      element.style.width = 'auto';
      element.style.minWidth = '0px';
      element.style.maxWidth = `${URL_NODE_COMPACT_MAX_WIDTH}px`;
      element.style.fontSize = `${nodeFontSize}px`;
      element.style.whiteSpace = 'normal';
      element.style.height = 'auto';
      return;
    }

    if (!renderedText.trim()) {
      element.style.width = (element.baseWidth || 200) + 'px';
      element.style.fontSize = `${nodeFontSize}px`;
      element.style.whiteSpace = 'nowrap';
      element.style.height = 'auto';
      return;
    }

    // Create measurer for current text
    const measurer = document.createElement('span');
    measurer.style.visibility = 'hidden';
    measurer.style.position = 'absolute';
    measurer.style.fontSize = `${nodeFontSize}px`;
    measurer.style.fontWeight = '500';
    measurer.style.fontFamily = nodeFontFamily;
    measurer.style.whiteSpace = 'pre';
    measurer.style.padding = `0 ${measurementPadding}px`;
    measurer.textContent = renderedText;
    document.body.appendChild(measurer);

    const textWidth = measurer.offsetWidth;
    document.body.removeChild(measurer);

    const mediumWidth = element.mediumWidth || 220;
    const maxWidth = isSelected ? NODE_EXPANDED_MAX_WIDTH : NODE_COMPACT_MAX_WIDTH;

    if (textWidth <= mediumWidth) {
      // Phase 1: Normal growth, 14px font, no wrapping
      element.style.width = Math.max(textWidth, element.baseWidth || 200) + 'px';
      element.style.fontSize = `${nodeFontSize}px`;
      element.style.whiteSpace = 'nowrap';
      element.style.height = 'auto';
    } else if (textWidth <= maxWidth) {
      // Phase 2: Continue growing but drop font to 12px
      element.style.fontSize = `${compactFontSize}px`;
      element.style.whiteSpace = 'nowrap';
      element.style.height = 'auto';

      // Remeasure with smaller font
      const smallMeasurer = document.createElement('span');
      smallMeasurer.style.visibility = 'hidden';
      smallMeasurer.style.position = 'absolute';
      smallMeasurer.style.fontSize = `${compactFontSize}px`;
      smallMeasurer.style.fontWeight = '500';
      smallMeasurer.style.fontFamily = nodeFontFamily;
      smallMeasurer.style.whiteSpace = 'nowrap';
      smallMeasurer.style.padding = `0 ${measurementPadding}px`;
      smallMeasurer.textContent = renderedText;
      document.body.appendChild(smallMeasurer);

      const smallTextWidth = smallMeasurer.offsetWidth;
      document.body.removeChild(smallMeasurer);

      element.style.width = Math.min(smallTextWidth, maxWidth) + 'px';
    } else {
      // Phase 3: Fixed width, wrapping enabled, 12px font. NodeContent
      // controls whether the text is clamped or fully shown when selected.
      element.style.width = maxWidth + 'px';
      element.style.fontSize = `${compactFontSize}px`;
      element.style.whiteSpace = 'normal';
      element.style.height = 'auto';
    }
    if (renderedText.includes('\n')) element.style.whiteSpace = 'pre-wrap';
  }, [
    attachment?.kind,
    isEditing,
    isSelected,
    linkUrl,
    measurementPadding,
    nodeFontFamily,
    nodeFontSize,
    compactFontSize,
    semanticZoom,
    semanticZoomSize?.height,
    semanticZoomSize?.width,
    text,
  ]);

  const notifyLayoutSize = useCallback(() => {
    if (semanticZoom) return;
    const element = divRef.current;
    if (!element) return;
    const width = element.offsetWidth;
    const height = element.offsetHeight;
    const typographyChanged = measuredTypographyRef.current.family !== nodeFontFamily || measuredTypographyRef.current.size !== nodeFontSize;
    measuredTypographyRef.current = { family: nodeFontFamily, size: nodeFontSize };
    const options = { transient: (isSelected || isEditing) && !typographyChanged };
    if (isEditing) options.editing = true;
    onSizeChange?.(
      id,
      Number.isFinite(width) && width > 0 && Number.isFinite(height) && height > 0
        ? { width, height }
        : null,
      options
    );
  }, [id, isEditing, isSelected, onSizeChange, semanticZoom, nodeFontFamily, nodeFontSize]);

  // Initialize base width measurement (re-measure if the theme font changes)
  useEffect(() => {
    const measurer = document.createElement('span');
    measurer.style.visibility = 'hidden';
    measurer.style.position = 'absolute';
    measurer.style.fontSize = `${nodeFontSize}px`;
    measurer.style.fontWeight = '500';
    measurer.style.fontFamily = nodeFontFamily;
    measurer.style.whiteSpace = 'pre';
    measurer.style.padding = `0 ${measurementPadding}px`;
    measurer.textContent = nodeMinimumTextSample;
    document.body.appendChild(measurer);

    const baseWidth = measurer.offsetWidth;
    baseWidthRef.current = baseWidth;
    document.body.removeChild(measurer);

    if (divRef.current) {
      divRef.current.baseWidth = baseWidth;
      divRef.current.mediumWidth = Math.max(baseWidth * 1.5, 220);
      divRef.current.style.width = baseWidth + 'px';
      divRef.current.style.minWidth = baseWidth + 'px';
    }
  }, [nodeFontFamily, nodeFontSize, measurementPadding, nodeMinimumTextSample]);

  // Update text when prop changes (for undo/redo) – but
  // *don't* overwrite the live editable DOM while editing,
  // as that can fight with the caret and input order.
  useEffect(() => {
    if (!isEditing && initialText !== text) {
      setText(initialText);
    }
  }, [initialText, text, isEditing, updateOvalSize, onSizeChange]);

  // Keep size in sync with current text
  useEffect(() => {
    if (divRef.current) {
      updateOvalSize(divRef.current);
      notifyLayoutSize();
    }
  }, [text, isSelected, updateOvalSize, notifyLayoutSize]);

  useEffect(() => {
    const element = divRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return undefined;
    let frameId = null;
    const observer = new ResizeObserver(() => {
      if (frameId !== null) cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        frameId = null;
        notifyLayoutSize();
      });
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
      if (frameId !== null) cancelAnimationFrame(frameId);
    };
  }, [notifyLayoutSize]);

  // Clear local position when external position matches
  useEffect(() => {
    if (lockPositionToState) {
      if (localPosition) {
        setLocalPosition(null);
      }
      return;
    }

    if (!isDragging && localPosition && x === localPosition.x && y === localPosition.y) {
      setLocalPosition(null);
    }
  }, [x, y, isDragging, localPosition, lockPositionToState]);

  const [referenceDismissedText, setReferenceDismissedText] = useState(null);
  const [referenceIndex, setReferenceIndex] = useState(0);
  const referenceOpen = Boolean(isEditing && text.startsWith('#') && !text.includes('\n')
    && text !== referenceDismissedText && onChooseIdeaReference);
  const referenceItems = useMemo(() => referenceOpen ? referenceCandidates.filter(candidate => (
    candidate.id !== id && candidate.label.toLocaleLowerCase().includes(text.slice(1).toLocaleLowerCase())
  )) : [], [referenceOpen, referenceCandidates, id, text]);
  const activeReferenceIndex = Math.min(referenceIndex, Math.max(0, referenceItems.length - 1));
  const activeReferenceId = referenceOpen ? referenceItems[activeReferenceIndex]?.id || null : null;
  useEffect(() => {
    // Candidate objects can change during layout or collaborative updates.
    // Only change the preview when its target changes, without clearing it first.
    onHighlightIdeaReference?.(activeReferenceId);
  }, [activeReferenceId, onHighlightIdeaReference]);
  useEffect(() => () => onHighlightIdeaReference?.(null), [onHighlightIdeaReference]);
  const chooseReference = useCallback((candidate) => {
    const nextText = `#${candidate.label}`;
    setText(nextText);
    setReferenceDismissedText(nextText);
    onChooseIdeaReference(candidate.id, candidate.label);
    requestAnimationFrame(() => inputRef.current?.blur());
  }, [onChooseIdeaReference]);

  const handleChange = useCallback((e) => {
    const newText = e.target.value;
    setText(newText);
    setReferenceIndex(0);
    onTextChange?.(newText);
  }, [onTextChange]);

  const handleKeyDown = useCallback((e) => {
    if (isEditing) {
      e.stopPropagation();
      if (e.nativeEvent?.isComposing || e.isComposing) return;
      if (referenceOpen) {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          setReferenceIndex(previous => Math.max(0, Math.min(referenceItems.length - 1, previous + (e.key === 'ArrowDown' ? 1 : -1))));
          return;
        }
        if (e.key === 'Enter' && !e.shiftKey && referenceItems[activeReferenceIndex]) {
          e.preventDefault();
          chooseReference(referenceItems[activeReferenceIndex]);
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          setReferenceDismissedText(text);
          return;
        }
      }
      if (e.key === 'Enter' && e.shiftKey) return;
      if (e.key === 'Enter') {
        // Enter: Exit editing mode
        e.preventDefault();
        inputRef.current?.blur();
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        inputRef.current?.blur();
        return;
      }

      // Don't let other keys bubble up while editing
      e.stopPropagation();
    }
  }, [isEditing, referenceOpen, referenceItems, activeReferenceIndex, chooseReference, text]);

  const beginDragAt = useCallback((point) => {
    // Selection is independent from content/position ownership. Keep the
    // pointer origin even when dragging is disallowed so the click handler can
    // still select a collaborator-created or view-only node.
    mouseDownPos.current = { x: point.clientX, y: point.clientY };
    mouseDragActivatedRef.current = false;
    if (!canMove) return false;
    const pointer = screenToCanvasPosition
      ? screenToCanvasPosition({ x: point.clientX, y: point.clientY })
      : { x: point.clientX, y: point.clientY };
    dragOffsetRef.current = {
      x: pointer.x - x,
      y: pointer.y - y,
    };
    setIsDragging(true);
    onDragStart?.({ clientX: point.clientX, clientY: point.clientY });
    return true;
  }, [canMove, x, y, onDragStart, screenToCanvasPosition]);

  const handleMouseDown = useCallback((e) => {
    if (Date.now() < suppressMouseUntil.current || e.button !== 0) return;
    beginDragAt(e);
  }, [beginDragAt]);

  const performDragMove = useCallback((point) => {
    if (isEditing || !canMove) return;
    const start = mouseDownPos.current;
    if (
      !mouseDragActivatedRef.current
      && start
      && Math.hypot(point.clientX - start.x, point.clientY - start.y)
        <= DESKTOP_DRAG_THRESHOLD_PX
    ) {
      return;
    }
    mouseDragActivatedRef.current = true;
    const pointer = screenToCanvasPosition
      ? screenToCanvasPosition({ x: point.clientX, y: point.clientY })
      : { x: point.clientX, y: point.clientY };
    const newPosition = {
      x: pointer.x - dragOffsetRef.current.x,
      y: pointer.y - dragOffsetRef.current.y,
    };

    if (!lockPositionToState) {
      setLocalPosition(newPosition);
    }

    onPositionChange?.(newPosition);
    onDragMove?.(newPosition, { clientX: point.clientX, clientY: point.clientY });
  }, [canMove, isEditing, lockPositionToState, onDragMove, onPositionChange, screenToCanvasPosition]);

  const handleMouseMove = useCallback((e) => {
    if (isDragging) performDragMove(e);
  }, [isDragging, performDragMove]);

  const handleMouseUp = useCallback(() => {
    if (mouseDragActivatedRef.current) {
      suppressClickUntilRef.current = Date.now() + 500;
    }
    setIsDragging(false);
    mouseDragActivatedRef.current = false;
    onDragComplete?.();
    onDragEnd?.();
  }, [onDragComplete, onDragEnd]);

  const touchPointerId = useRef(null);
  const touchStartPoint = useRef(null);
  const longPressTimeout = useRef(null);
  const contextOpenTimeout = useRef(null);
  const longPressTriggered = useRef(false);
  const touchDragStarted = useRef(false);
  const suppressNativeContextUntil = useRef(0);

  const clearLongPress = useCallback(() => {
    if (longPressTimeout.current !== null) {
      clearTimeout(longPressTimeout.current);
      longPressTimeout.current = null;
    }
  }, []);

  const clearContextOpen = useCallback(() => {
    if (contextOpenTimeout.current !== null) {
      clearTimeout(contextOpenTimeout.current);
      contextOpenTimeout.current = null;
    }
  }, []);

  const handlePointerDown = useCallback((event) => {
    if (event.pointerType === 'mouse' || event.defaultPrevented) return;
    clearLongPress();
    longPressTriggered.current = false;
    touchDragStarted.current = false;
    touchPointerId.current = event.pointerId;
    touchStartPoint.current = { x: event.clientX, y: event.clientY };
    mouseDownPos.current = { x: event.clientX, y: event.clientY };
    suppressNativeContextUntil.current = Date.now() + 2_000;
    event.currentTarget?.setPointerCapture?.(event.pointerId);
    suppressMouseUntil.current = Date.now() + 700;
    longPressTimeout.current = setTimeout(() => {
      longPressTimeout.current = null;
      longPressTriggered.current = true;
      mouseDownPos.current = null;
    }, NODE_LONG_PRESS_DELAY_MS);
  }, [clearLongPress]);

  const handlePointerMove = useCallback((event) => {
    if (event.pointerType === 'mouse' || touchPointerId.current !== event.pointerId) return;
    event.preventDefault();
    if (longPressTriggered.current) return;
    const start = touchStartPoint.current;
    if (start && Math.hypot(event.clientX - start.x, event.clientY - start.y) <= NODE_LONG_PRESS_MOVE_TOLERANCE) {
      return;
    }
    clearLongPress();
    if (!touchDragStarted.current && start) {
      touchDragStarted.current = true;
      beginDragAt({ clientX: start.x, clientY: start.y });
    }
    performDragMove(event);
  }, [beginDragAt, clearLongPress, performDragMove]);

  const handlePointerUp = useCallback((event) => {
    if (event.pointerType === 'mouse' || touchPointerId.current !== event.pointerId) return;
    clearLongPress();
    const completedLongPress = longPressTriggered.current;
    const completedDrag = touchDragStarted.current;
    const pressPoint = touchStartPoint.current;
    touchPointerId.current = null;
    touchStartPoint.current = null;
    touchDragStarted.current = false;
    event.currentTarget?.releasePointerCapture?.(event.pointerId);
    if (completedLongPress) {
      event.preventDefault();
      event.stopPropagation();
      longPressTriggered.current = false;
      setIsDragging(false);
      clearContextOpen();
      contextOpenTimeout.current = setTimeout(() => {
        contextOpenTimeout.current = null;
        onContextMenu?.(id, {
          clientX: pressPoint?.x ?? event.clientX,
          clientY: pressPoint?.y ?? event.clientY,
        });
      }, 0);
      return;
    }
    if (completedDrag) handleMouseUp();
  }, [clearContextOpen, clearLongPress, handleMouseUp, id, onContextMenu]);

  const cancelTouchGesture = useCallback(() => {
    clearLongPress();
    clearContextOpen();
    const pointerId = touchPointerId.current;
    const completedDrag = touchDragStarted.current;
    touchPointerId.current = null;
    touchStartPoint.current = null;
    touchDragStarted.current = false;
    longPressTriggered.current = false;
    mouseDownPos.current = null;

    if (pointerId !== null) {
      try {
        divRef.current?.releasePointerCapture?.(pointerId);
      } catch {
        // Pointer capture is optional in test DOMs and may already be released.
      }
    }

    if (completedDrag) {
      handleMouseUp();
    } else {
      setIsDragging(false);
    }
    return { hadActiveDrag: completedDrag };
  }, [clearContextOpen, clearLongPress, handleMouseUp]);

  // Expose direct DOM/layout and gesture methods to the canvas. In particular,
  // cancellation lets a second touch transfer a node-origin gesture to pinch.
  useImperativeHandle(ref, () => ({
    focus: () => {
      if (isEditing) {
        inputRef.current?.focus({ preventScroll: true });
      } else {
        divRef.current?.focus({ preventScroll: true });
      }
    },
    getBoundingClientRect: () => divRef.current?.getBoundingClientRect(),
    getFontSize: () => {
      const element = divRef.current?.querySelector('.node-content') || divRef.current;
      return element ? Number.parseFloat(window.getComputedStyle(element).fontSize) : nodeFontSize;
    },
    offsetParent: divRef.current?.offsetParent,
    offsetLeft: divRef.current?.offsetLeft,
    offsetTop: divRef.current?.offsetTop,
    getDOM: () => divRef.current,
    getLayoutSize: () => {
      const element = divRef.current;
      if (!element) return null;
      return { width: element.offsetWidth, height: element.offsetHeight };
    },
    getSize: () => {
      const rect = divRef.current?.getBoundingClientRect();
      return rect ? { width: rect.width, height: rect.height } : { width: 200, height: 60 };
    },
    startEditing: () => {
      beginEditing();
    },
    stopEditing: () => {
      inputRef.current?.blur();
    },
    isEditing: () => isEditing,
    cancelTouchGesture,
  }), [isEditing, beginEditing, cancelTouchGesture, nodeFontSize]);

  useEffect(() => () => {
    clearLongPress();
    clearContextOpen();
    if (previewTimeoutRef.current) clearTimeout(previewTimeoutRef.current);
    if (mapLinkTimeoutRef.current) clearTimeout(mapLinkTimeoutRef.current);
  }, [clearContextOpen, clearLongPress]);

  const handleClick = useCallback((e) => {
    e.stopPropagation();

    // Ignore non-primary button clicks (e.g. right-click)
    if (e.button !== 0) {
      return;
    }

    if (!mouseDownPos.current) return;

    const end = { x: e.clientX, y: e.clientY };
    const start = mouseDownPos.current;
    const distance = Math.hypot(end.x - start.x, end.y - start.y);

    if (distance > DESKTOP_DRAG_THRESHOLD_PX) return; // treat as drag, not click
    if (isEditing) return;
    if (e.detail < 2) onSelectionGlide?.(id);

    if (isEmbeddedMindmapLink && !embeddedMindmapId) {
      // Visually a map-link but backend id not yet assigned;
      // treat as non-navigating until the link is fully ready.
      if (!isSelected && onSelect) {
        onSelect(id);
      }
      return;
    }

    const embeddedMapHasMeaningfulMetadata = Boolean(
      (typeof text === 'string' && text.trim() && text.trim() !== 'New Map')
      || (linkMeta?.title && String(linkMeta.title).trim() !== 'New Map')
      || linkMeta?.description
      || linkMeta?.image
    );
    if (embeddedMindmapId && !embeddedMapHasMeaningfulMetadata) {
      if (!isSelected && onSelect) {
        onSelect(id);
      }
      if (mapLinkTimeoutRef.current) {
        clearTimeout(mapLinkTimeoutRef.current);
      }
      mapLinkTimeoutRef.current = setTimeout(() => {
        if (typeof window !== 'undefined') {
          const baseUrl = `${window.location.origin}${window.location.pathname}`;
          const targetUrl = linkUrl
            || `${baseUrl}?map=${encodeURIComponent(embeddedMindmapId)}`;
          window.open(targetUrl, '_blank', 'noopener,noreferrer');
        }
        mapLinkTimeoutRef.current = null;
      }, 220);
      return;
    }

    // Cancel any pending preview from a previous click
    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current);
      previewTimeoutRef.current = null;
    }
    if (mapLinkTimeoutRef.current) {
      clearTimeout(mapLinkTimeoutRef.current);
      mapLinkTimeoutRef.current = null;
    }

    // For URL-only nodes, a single click should both select the node
    // and toggle the inline preview card. To keep double-click
    // reserved for editing (instead of briefly opening the preview
    // and potentially navigating), we defer opening the preview
    // into a short timeout that the double-click handler can cancel.
    const url = getUrlIfUrlOnly(text, linkUrl);
    const sameMapIdeaTarget = getMindmappiIdeaTarget(url, mindmapId);
    if (sameMapIdeaTarget && onNavigateIdeaReference) {
      if (!isSelected && onSelect) onSelect(id);
      return;
    }
    if (url && onOpenPreview) {
      const forceRefetchMeta = isMindmappiMapUrl(url);
      if (!isSelected && onSelect) {
        onSelect(id);
      }
      previewTimeoutRef.current = setTimeout(() => {
        setIsPreviewOpen(prev => !prev);
        if (!linkMeta || forceRefetchMeta) {
          onOpenPreview(id, url);
        }
        previewTimeoutRef.current = null;
      }, 220);
      return;
    }

    // Default behavior: single click selects the node.
    if (!isSelected && onSelect) {
      onSelect(id);
    }
  }, [id, isSelected, isEditing, mindmapId, onSelect, onSelectionGlide, onOpenPreview, onNavigateIdeaReference, text, linkUrl, linkMeta, embeddedMindmapId, isEmbeddedMindmapLink]);

  const handleIdeaReferenceClick = useCallback((event) => {
    event.preventDefault();
    // First select the idea so its actions remain accessible, even when the
    // initial click lands on its label. Follow the link once it is selected.
    if (!isSelected && event.detail !== 0) return;
    event.stopPropagation();
    const target = getMindmappiIdeaTarget(getUrlIfUrlOnly(text, linkUrl), mindmapId);
    if (!target || !onNavigateIdeaReference) return;
    if (previewTimeoutRef.current) clearTimeout(previewTimeoutRef.current);
    // Preserve double-click editing by letting its handler cancel navigation.
    previewTimeoutRef.current = setTimeout(() => {
      onNavigateIdeaReference(target);
      previewTimeoutRef.current = null;
    }, 220);
  }, [isSelected, text, linkUrl, mindmapId, onNavigateIdeaReference]);

  const handleDoubleClick = useCallback((e) => {
    // Dedicated double-click handler for entering edit mode.
    e.preventDefault();
    e.stopPropagation();
    if (attachment?.assetId) {
      onOpenAttachment?.(attachment);
      return;
    }
    if (!canEdit) {
      beginEditing();
      return;
    }
    // Cancel any pending preview so double-click always edits
    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current);
      previewTimeoutRef.current = null;
    }
    if (mapLinkTimeoutRef.current) {
      clearTimeout(mapLinkTimeoutRef.current);
      mapLinkTimeoutRef.current = null;
    }
    setIsPreviewOpen(false);
    // When the user explicitly chooses to edit this node,
    // restore the underlying URL into the text (when we
    // know it) so they're editing the actual link rather
    // than the fetched title. Also clear any stored link
    // metadata so subsequent URL detection treats it as
    // a fresh node.
    let urlForEditing = null;
    const editingIdeaReference = getMindmappiIdeaTarget(linkUrl, mindmapId) && text.startsWith('#');
    if (linkUrl && !embeddedMindmapId && !editingIdeaReference) {
      urlForEditing = linkUrl;
    } else if (typeof text === 'string') {
      const trimmed = text.trim();
      if (urlOnlyRegex.test(trimmed)) {
        urlForEditing = trimmed;
      }
    }

    if (urlForEditing) {
      setText(urlForEditing);
      onTextChange?.(urlForEditing);
    }

    if (!embeddedMindmapId && !editingIdeaReference) {
      onClearLinkMeta?.();
    }
    beginEditing();
  }, [
    beginEditing,
    attachment,
    onOpenAttachment,
    canEdit,
    embeddedMindmapId,
    onClearLinkMeta,
    linkUrl,
    mindmapId,
    text,
    onTextChange,
  ]);

  const handleClickCapture = useCallback((event) => {
    if (Date.now() > suppressClickUntilRef.current) {
      suppressClickUntilRef.current = 0;
      return;
    }
    suppressClickUntilRef.current = 0;
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleBlur = useCallback(() => {
    const wasEditing = isEditingRef.current;
    isEditingRef.current = false;
    setIsEditing(false);
    if (!wasEditing) return;
    const committedText = typeof text === 'string' ? text : '';
    if (embeddedMindmapId && onMapLinkTitleCommit) {
      onMapLinkTitleCommit(committedText.trim());
    }
    // Pass the final text value and node id to the container so it can
    // persist exactly what the user sees, even if state updates have
    // not fully flushed yet.
    editComplete?.(committedText, id);
  }, [editComplete, embeddedMindmapId, onMapLinkTitleCommit, text, id]);

  // Mouse dragging uses document listeners; touch uses pointer capture on the node.
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // When the node is deselected (e.g. user clicks outside), close
  // any open inline preview so it returns to the compact view.
  useEffect(() => {
    if (!isSelected && isPreviewOpen) {
      setIsPreviewOpen(false);
    }
  }, [isSelected, isPreviewOpen]);

  // Notify parent container when the inline preview opens or closes,
  // since this changes the rendered node size and we need lines and
  // layout that depend on bounding boxes to update accordingly.
  useEffect(() => {
    notifyLayoutSize();
  }, [isPreviewOpen, notifyLayoutSize]);

  // Calculate expand/collapse button info
  const getExpandCollapseInfo = useCallback(() => {
    // If node has collapsed children, show + to expand
    if (collapsedChildren && collapsedChildren.nodes && collapsedChildren.nodes.length > 0) {

      // Analyze child positions to determine predominant direction
      const childPositions = collapsedChildren.nodes.map(child => {
        if (child.position && typeof child.position.angle !== 'undefined') {
          const angle = child.position.angle;
          const normalizedAngle = ((angle % 360) + 360) % 360;
          return normalizedAngle > 90 && normalizedAngle < 270 ? 'left' : 'right';
        }
        return 'right';
      });

      const rightChildren = childPositions.filter(pos => pos === 'right').length;
      const leftChildren = childPositions.filter(pos => pos === 'left').length;
      const isRightSide = rightChildren >= leftChildren;

      return {
        type: 'expand',
        symbol: '+',
        direction: isRightSide ? 'right' : 'left',
        count: collapsedChildren.nodes.length,
        tooltip: `Click to expand ${collapsedChildren.nodes.length} hidden ideas`
      };
    }

    // If node has visible children, show - to collapse
    if (hasChildren) {
      const currentChildren = getChildrenPositions ? getChildrenPositions(id) : [];
      const currentPosition = localPosition || { x, y };
      let leftRight = 'right';
      if(currentPosition){
        const childPositions = currentChildren.map(child => child.x);
        const parentX = currentPosition.x;
        const rightChildren = childPositions.filter(x => x > parentX).length;
        const leftChildren = childPositions.filter(x => x <= parentX).length;
        const isRightSide = rightChildren >= leftChildren;
        leftRight = isRightSide ? 'right' : 'left';
      }

      return {
        type: 'collapse',
        symbol: '−', // Using minus sign (−) instead of hyphen (-)
        direction: leftRight, // Default position for collapse button
        count: 0,
        tooltip: 'Click to collapse children'
      };
    }

    return null;
  }, [collapsedChildren, hasChildren, localPosition, x, y, getChildrenPositions, id]);

  const buttonInfo = getExpandCollapseInfo();

  // Handle expand/collapse button click
  const handleButtonClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    if (buttonInfo?.type === 'expand') {
      onExpandNode?.(id);
    } else if (buttonInfo?.type === 'collapse') {
      onCollapseNode?.(id);
    }
  }, [id, buttonInfo, onExpandNode, onCollapseNode]);

  // Use localPosition during drag for immediate visual feedback, otherwise use props
  const currentPosition = lockPositionToState
    ? { x, y }
    : (localPosition || { x, y });

  const urlForPreview = getUrlIfUrlOnly(text, linkUrl);
  const sameMapIdeaTarget = getMindmappiIdeaTarget(urlForPreview, mindmapId);
  const isUrlNode = !!urlForPreview;
  const isCardNode = !semanticZoom && (isUrlNode || Boolean(attachment));
  const isImageAttachment = attachment?.kind === 'image';
  const isCompactImageAttachment = isImageAttachment && !isSelected && !semanticZoom;
  const semanticZoomLabel = getSemanticZoomLabel(
    linkMeta?.title || attachment?.filename || text
  );
  const linkProvider = getLinkProvider(urlForPreview);
  const isYouTubeLink = linkProvider === 'youtube';
  const isInstagramLink = linkProvider === 'instagram';
  const isSpotifyLink = linkProvider === 'spotify';
  const isMindmappiLink = linkProvider === 'mindmappi';
  const isMindmappiIdeaLink = isMindmappiLink
    && linkMeta?.resourceType === 'mindmappi_idea';

  let previewDomain = null;
  let previewFaviconUrl = null;
  if (urlForPreview) {
    try {
      const u = new URL(urlForPreview);
      previewDomain = u.hostname.replace(/^www\./, '');
      const isLocalHost =
        u.hostname === 'localhost' ||
        u.hostname === '127.0.0.1';
      const faviconBase =
        typeof window !== 'undefined' && isLocalHost
          ? window.location.origin
          : u.origin;
      previewFaviconUrl = `${faviconBase}/favicon.ico`;
    } catch (e) {
      previewDomain = null;
      previewFaviconUrl = null;
    }
  }
  const previewProviderLabel = getLinkProviderLabel(
    linkProvider,
    linkMeta,
    previewDomain
  );

  const nameSource = creatorName || userId;
  const createdByOther = !!userId && !!currentUserId && userId !== currentUserId;
  const editorInitials = createdByOther && nameSource ? getEditorInitials(nameSource) : '';

  // Theme overrides merged over the standard node style. The stored node
  // color is untouched; the theme only reinterprets how it renders.
  const themeColor = getThemeNodeColor(theme, color) || color;
  const themeNodeStyle = getThemeNodeStyle(theme, {
    color,
    themeColor,
    isRoot,
    isSelected,
    isHighlighted,
    isDragging,
    isEditing,
    isCardNode,
  });

  return (
    <div
      data-node-drop-id={id}
      data-node-position-animating={isPositionAnimating ? 'true' : 'false'}
      aria-hidden={isContextOnly ? true : undefined}
      inert={isContextOnly ? true : undefined}
      style={{
        position: 'absolute',
        left: `${currentPosition.x}px`,
        top: `${currentPosition.y}px`,
        transition: isPositionAnimating
          ? 'left 180ms cubic-bezier(0.2, 0.8, 0.2, 1), top 180ms cubic-bezier(0.2, 0.8, 0.2, 1)'
          : 'none',
        // Ensure opened-preview nodes float above others
        zIndex: isPreviewOpen ? 30 : (isSelected ? 20 : 1),
        pointerEvents: isContextOnly ? 'none' : 'auto',
      }}
    >
      {/* Keep connections occluded while the foreground fades into the canvas. */}
      <div className="node-underlay" aria-hidden="true" style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        background: getThemeCanvasSolidColor(theme, isDarkMode),
        borderRadius: isCompactImageAttachment ? '10px' : (themeNodeStyle?.borderRadius || (isCardNode ? '24px' : '50px')),
        transform: isSelected ? 'scale(1.15)' : 'scale(1)',
        transition: 'transform 320ms ease',
      }} />
      <div
        id={getIdeaDomId(id)}
        className="node"
        data-node-id={id}
        data-node-context={isContextOnly ? 'true' : 'false'}
        data-node-root={isRoot ? 'true' : 'false'}
        data-node-selected={isSelected ? 'true' : 'false'}
        data-node-moderation={moderationNotice ? 'warning' : undefined}
        data-node-detail={semanticZoom ? 'simplified' : 'full'}
        role={isContextOnly ? 'presentation' : 'treeitem'}
        aria-label={isContextOnly ? undefined : ((text || '').trim() || 'Untitled idea')}
        aria-selected={isContextOnly ? undefined : isSelected}
        ref={divRef}
        onMouseDown={handleMouseDown}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onMouseEnter={() => {
          if (showCollapseButtonTimeout.current) {
            clearTimeout(showCollapseButtonTimeout.current);
          }
          setShowCollapseButton(true);
        }}
        onMouseLeave={() => {
          showCollapseButtonTimeout.current = setTimeout(() => {
            setShowCollapseButton(false);
          }, 300);
        }}
        onClickCapture={handleClickCapture}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (Date.now() < suppressNativeContextUntil.current) return;
          if (onContextMenu) {
            onContextMenu(id, e);
          }
        }}
        onBlur={handleBlur}
        style={{
          fontWeight: isRoot ? 700 : 500,
          position: 'relative', // Changed to relative since parent handles positioning
          direction: 'ltr',
          background: themeColor,
          borderRadius: isCardNode ? '24px' : '50px',
          padding: isPreviewOpen
            ? (isCardNode ? '10px 14px' : `10px ${nodeHorizontalPadding}px`)
            : (isImageAttachment
              ? '6px'
              : (isCardNode ? '7px 12px' : `8px ${nodeHorizontalPadding}px`)),
          color: 'white',
          fontSize: `${nodeFontSize}px`,
          textAlign: isCardNode ? 'left' : 'center',
          outline: 'none',
          border: 'none',
          cursor: isEditing ? 'text' : !canMove ? 'default' : isDragging ? 'grabbing' : 'grab',
          whiteSpace: isPreviewOpen || isCardNode ? 'normal' : 'nowrap',
          overflowWrap: 'anywhere',
          overflow: 'hidden',
          minWidth: isImageAttachment
            ? '0px'
            : isUrlNode && !isSelected
            ? '0px'
            : `${baseWidthRef.current || NODE_MIN_WIDTH_FALLBACK}px`,
          maxWidth: `${isUrlNode && !isSelected
            ? URL_NODE_COMPACT_MAX_WIDTH
            : (isSelected ? NODE_EXPANDED_MAX_WIDTH : NODE_COMPACT_MAX_WIDTH)}px`,
          maxHeight: isEditing ? '70vh' : undefined,
          width: 'auto',
          boxSizing: semanticZoom || (isUrlNode && !isSelected) ? 'border-box' : 'content-box',
          transform: isSelected ? 'scale(1.15)' : 'scale(1)',
          transition: animate
            ? 'all 0.3s ease'
            : 'box-shadow 0.3s ease, opacity 300ms ease, transform 320ms ease',
          boxShadow: (isSelected || isDragging)
            ? '0 8px 24px rgba(0,0,0,0.35), 0 2px 8px rgba(0,0,0,0.15)'
            : isHighlighted
            ? '0 0 0 3px rgba(0, 122, 204, 0.5), 0 4px 12px rgba(0,0,0,0.2)'
            : '0 2px 8px rgba(0,0,0,0.15)',
          lineHeight: '1.2',
          display: 'flex',
          flexDirection: isPreviewOpen || attachment ? 'column' : 'row',
          alignItems: isImageAttachment
            ? 'center'
            : (isPreviewOpen || attachment ? 'stretch' : 'center'),
          justifyContent: isPreviewOpen || attachment ? 'flex-start' : 'center',
          minHeight: isCardNode ? '26px' : '18px',
          fontFamily: nodeFontFamily,
          '--idea-font-size': fontSize == null ? undefined : `${nodeFontSize}px`,
          userSelect: isEditing ? 'text' : 'none',
          WebkitUserSelect: isEditing ? 'text' : 'none',
          touchAction: isEditing ? 'manipulation' : 'none',
          opacity: opacity,
          zIndex: isSelected ? 10 : 1,
          ...themeNodeStyle,
          ...(isCompactImageAttachment ? {
            background: 'transparent',
            border: 'none',
            borderRadius: '10px',
            padding: '0px',
            boxShadow: isHighlighted
              ? '0 0 0 3px rgba(0, 122, 204, 0.5)'
              : 'none',
          } : {}),
        }}
      >
        {!semanticZoom && createdByOther && editorInitials && (
          <div
            className="node-editor-badge"
            style={{
              background: themeColor,
              color: themeNodeStyle?.color || 'white',
              opacity: 0.85,
            }}
          >
            {editorInitials}
          </div>
        )}
        {/* Visible text when not editing */}
        {!isEditing && (
          semanticZoom ? (
            <span
              className="node-semantic-zoom-label"
              style={{
                display: 'block',
                width: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textAlign: 'center',
                padding: '0 8px',
                boxSizing: 'border-box',
              }}
            >
              {semanticZoomLabel}
            </span>
          ) : (
          <>
            {attachment ? (
              <div
                className={`node-attachment node-attachment--${attachment.kind} ${isSelected ? 'node-attachment--selected' : ''}`}
              >
                {attachment.kind === 'image' ? (
                  <button
                    type="button"
                    className="node-attachment__image-button"
                    onClick={(event) => {
                      event.stopPropagation();
                      if (!isSelected) onSelect?.(id);
                      else onOpenAttachment?.(attachment);
                    }}
                    onDoubleClick={(event) => event.stopPropagation()}
                    aria-label={isSelected
                      ? `Open ${attachment.filename || 'image'}`
                      : `Select ${attachment.filename || 'image'}`}
                  >
                    {attachmentThumbnailUrl ? (
                      <img
                        src={attachmentThumbnailUrl}
                        alt={attachment.filename || 'Attached image'}
                        width={attachment.width || undefined}
                        height={attachment.height || undefined}
                        draggable="false"
                        className="node-attachment__image"
                      />
                    ) : (
                      <span className="node-attachment__image-placeholder" aria-hidden="true">Image</span>
                    )}
                  </button>
                ) : (
                  <div className="node-attachment__summary">
                    <span className="node-attachment__markdown-icon" aria-hidden="true">md</span>
                    <span className={`node-attachment__filename ${attachment.description ? 'node-attachment__description' : ''}`}>
                      {attachment.description || attachment.filename || text || 'Attachment'}
                    </span>
                  </div>
                )}
                {attachment.kind === 'markdown' && isSelected && (
                  <div className="node-attachment__actions">
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        onOpenAttachment?.(attachment);
                      }}
                    >
                      Open
                    </button>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        onDownloadAttachment?.(attachment);
                      }}
                    >
                      Download
                    </button>
                  </div>
                )}
              </div>
            ) : (!isPreviewOpen || !urlForPreview) && (
              isTask ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    minWidth: 0,
                    maxWidth: '100%',
                    width: isUrlNode ? 'auto' : '100%',
                  }}
                >
                  <button
                    type="button"
                    disabled={!canCompleteTask}
                    aria-label={taskDone ? 'Mark task incomplete' : 'Mark task complete'}
                    title={canCompleteTask
                      ? (taskDone ? 'Mark task incomplete' : 'Mark task complete')
                      : 'Only the assignee or idea creator can complete this task'}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!canCompleteTask) {
                        onDisallowedEdit?.();
                        return;
                      }
                      onToggleTaskDone?.();
                    }}
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: 3,
                      border: '1.5px solid rgba(255,255,255,0.9)',
                      backgroundColor: taskDone ? 'rgba(255,255,255,0.9)' : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 0,
                      cursor: canCompleteTask ? 'pointer' : 'default',
                    }}
                  >
                    {taskDone && (
                      <span
                        style={{
                          fontSize: 9,
                          color: isUrlNode ? '#111827' : '#111827',
                        }}
                      >
                        ✓
                      </span>
                    )}
                  </button>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: emoji ? 2 : 0,
                      minWidth: 0,
                      maxWidth: '100%',
                      flex: 1,
                      textDecoration: taskDone ? 'line-through' : 'none',
                      opacity: taskDone ? 0.6 : 1,
                    }}
                  >
                    {emoji && (
                      <span
                        style={{
                          fontSize: 20,
                          lineHeight: 1,
                        }}
                      >
                        {emoji}
                      </span>
                    )}
                    <NodeContent
                      text={text && text.trim().length ? text : 'New idea'}
                      isEditing={false}
                      isSelected={isSelected}
                      linkUrl={linkUrl}
                      linkMeta={linkMeta}
                      sameMapIdeaTarget={sameMapIdeaTarget}
                      onIdeaReferenceClick={handleIdeaReferenceClick}
                    />
                  </div>
                  {responsiblePrincipal?.userId && (
                    <span
                      className="node-task-assignee"
                      aria-label={`Assigned to ${responsiblePrincipal.displayName || 'Mindmappi collaborator'}`}
                      title={`Assigned to ${responsiblePrincipal.displayName || 'Mindmappi collaborator'}`}
                    >
                      {responsibilityInitials(responsiblePrincipal.displayName)}
                    </span>
                  )}
                </div>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: emoji ? 2 : 0,
                    minWidth: 0,
                    maxWidth: '100%',
                    width: '100%',
                    justifyContent: 'center',
                  }}
                >
                  {emoji && (
                    <span
                      style={{
                        fontSize: 20,
                        lineHeight: 1,
                      }}
                    >
                      {emoji}
                    </span>
                  )}
                  <NodeContent
                    text={text && text.trim().length ? text : 'New idea'}
                    isEditing={false}
                    isSelected={isSelected}
                    linkUrl={linkUrl}
                    linkMeta={linkMeta}
                    sameMapIdeaTarget={sameMapIdeaTarget}
                    onIdeaReferenceClick={handleIdeaReferenceClick}
                  />
                </div>
              )
            )}
            {isPreviewOpen && urlForPreview && (
              <div
                style={{
                  marginTop: 1,
                  paddingTop: 2,
                  fontSize: 12,
                  textAlign: 'left',
                }}
              >
                {previewDomain && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 11,
                      opacity: 0.85,
                      marginBottom: 3,
                    }}
                  >
                    {isSpotifyLink ? (
                      <FaSpotify
                        aria-hidden="true"
                        style={{
                          width: 12,
                          height: 12,
                          color: '#1DB954',
                          flexShrink: 0,
                        }}
                      />
                    ) : isInstagramLink ? (
                      <div
                        style={{
                          width: 11,
                          height: 11,
                          borderRadius: 3.5,
                          background:
                            'radial-gradient(circle at 30% 30%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 70%, #285AEB 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <div
                          style={{
                            width: 6,
                            height: 6,
                            borderRadius: 2,
                            border: '1.5px solid white',
                            position: 'relative',
                            boxSizing: 'border-box',
                          }}
                        >
                          <div
                            style={{
                              width: 2,
                              height: 2,
                              borderRadius: '50%',
                              border: '1.5px solid white',
                              position: 'absolute',
                              top: 0,
                              right: 0,
                              boxSizing: 'border-box',
                            }}
                          />
                        </div>
                      </div>
                    ) : (
                      previewFaviconUrl && (
                        <img
                          src={previewFaviconUrl}
                          alt=""
                          style={{
                            width: 11,
                            height: 11,
                            borderRadius: 3,
                            display: 'block',
                            flexShrink: 0,
                          }}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      )
                    )}
                    <span>{previewProviderLabel}</span>
                  </div>
                )}
                {linkMeta && linkMeta.image && !isMindmappiLink && (
                  <div
                    style={{
                      marginTop: 6,
                      borderRadius: 8,
                      overflow: 'hidden',
                      maxHeight: 165,
                      position: 'relative',
                    }}
                  >
                    <a
                      href={urlForPreview}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={
                        isSpotifyLink
                          ? `Open ${linkMeta.title || 'item'} in Spotify`
                          : `Open ${linkMeta.title || previewDomain || 'link'}`
                      }
                      onClick={(e) => e.stopPropagation()}
                      style={{ display: 'block' }}
                    >
                      <img
                        src={linkMeta.image}
                        alt={`${linkMeta.title || previewProviderLabel} preview`}
                        style={{
                          width: '100%',
                          display: 'block',
                          objectFit: isSpotifyLink ? 'contain' : 'cover',
                          cursor: 'pointer',
                        }}
                      />
                      {isYouTubeLink && (
                        <div
                          style={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            pointerEvents: 'none',
                          }}
                        >
                          <div
                            style={{
                              width: 56,
                              height: 40,
                              borderRadius: 12,
                              backgroundColor: 'rgba(0,0,0,0.55)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <div
                              style={{
                                width: 0,
                                height: 0,
                                borderTop: '8px solid transparent',
                                borderBottom: '8px solid transparent',
                                borderLeft: '14px solid white',
                                marginLeft: 3,
                              }}
                            />
                          </div>
                        </div>
                      )}
                      {isInstagramLink && !isYouTubeLink && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            width: 26,
                            height: 26,
                            borderRadius: 8,
                            background:
                              'radial-gradient(circle at 30% 30%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 70%, #285AEB 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
                            pointerEvents: 'none',
                          }}
                        >
                          <div
                            style={{
                              width: 14,
                              height: 14,
                              borderRadius: 4,
                              border: '2px solid white',
                              position: 'relative',
                              boxSizing: 'border-box',
                            }}
                          >
                            <div
                              style={{
                                width: 4,
                                height: 4,
                                borderRadius: '50%',
                                border: '2px solid white',
                                position: 'absolute',
                                top: 2,
                                right: 2,
                                boxSizing: 'border-box',
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </a>
                  </div>
                )}
                {linkMeta && linkMeta.title && !isMindmappiLink && (
                  <a
                    href={urlForPreview}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      color: 'inherit',
                      display: 'block',
                      fontWeight: 600,
                      marginTop: 6,
                      maxHeight: 40,
                      overflow: 'hidden',
                      textDecoration: 'none',
                    }}
                  >
                    {linkMeta.title}
                  </a>
                )}
                {linkMeta && linkMeta.description && (
                  <p
                    style={{
                      margin: '6px 0 0',
                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: 4,
                      overflow: 'hidden',
                      lineHeight: 1.35,
                      opacity: 0.92,
                    }}
                  >
                    {linkMeta.description}
                  </p>
                )}
                <a
                  href={urlForPreview}
                  className={isMindmappiLink ? 'node-map-open-link' : undefined}
                  target={isMindmappiIdeaLink ? '_self' : '_blank'}
                  rel="noopener noreferrer"
                  style={{
                    color: 'inherit',
                    display: isMindmappiLink ? 'flex' : 'block',
                    marginTop: isMindmappiLink ? 10 : 6,
                    fontSize: isMindmappiLink ? 14 : 11,
                    textDecoration: isMindmappiLink ? 'none' : 'underline',
                    cursor: 'pointer',
                    opacity: 0.9,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {isSpotifyLink
                    ? 'Open in Spotify'
                    : isMindmappiLink
                      ? isMindmappiIdeaLink
                        ? 'Open idea'
                        : 'Open map'
                      : `Open ${previewProviderLabel}`}
                </a>
              </div>
            )}
          </>
          )
        )}

        {isEditing && (
          <div
            data-node-editor-sizer={id}
            aria-hidden="true"
            style={{
              width: '100%',
              maxHeight: 'calc(70vh - 16px)',
              visibility: 'hidden',
              whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
              overflow: 'hidden',
              lineHeight: 'inherit',
              textAlign: 'center',
              pointerEvents: 'none',
            }}
          >
            {text || ' '}
          </div>
        )}

        {/* Textarea is always mounted so we can focus it synchronously
            from the double-click or startEditing handlers without
            waiting for a re-render. */}
        <textarea
          ref={inputRef}
          data-node-editor={id}
          aria-autocomplete="list"
          aria-haspopup="listbox"
          aria-controls={referenceOpen ? `idea-references-${id}` : undefined}
          aria-activedescendant={referenceOpen && referenceItems.length ? `idea-references-${id}-${activeReferenceIndex}` : undefined}
          aria-label={`Edit ${(text || '').trim() || 'untitled idea'}`}
          aria-hidden={!isEditing}
          value={text}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          tabIndex={isEditing ? 0 : -1}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            resize: 'none',
            border: 'none',
            outline: 'none',
            background: 'transparent',
            color: 'inherit',
            font: 'inherit',
            textAlign: 'center',
            overflowX: 'hidden',
            overflowY: 'auto',
            // Match the node's padding so the text doesn't
            // jump vertically when entering edit mode
            padding: `8px ${nodeHorizontalPadding}px`,
            boxSizing: 'border-box',
            margin: 0,
            opacity: isEditing ? 1 : 0,
            pointerEvents: isEditing ? 'auto' : 'none',
          }}
        />
      </div>

      {referenceOpen && <IdeaReferencePicker items={referenceItems} activeIndex={activeReferenceIndex}
        onActiveIndex={setReferenceIndex} onChoose={chooseReference} anchor={inputRef.current}
        theme={theme} isDarkMode={isDarkMode} listId={`idea-references-${id}`} />}

      {!semanticZoom && discussionSummary && onOpenDiscussion && (
        <button
          type="button"
          className={`node-discussion-indicator${discussionSummary.unreadCount > 0 ? ' node-discussion-indicator--unread' : ''}`}
          aria-label={
            discussionSummary.unreadCount > 0
              ? `Open idea discussion, ${discussionSummary.unreadCount} unread`
              : 'Open idea discussion'
          }
          title={
            discussionSummary.unreadCount > 0
              ? `${discussionSummary.unreadCount} unread discussion message${discussionSummary.unreadCount === 1 ? '' : 's'}`
              : 'Open idea discussion'
          }
          data-export-ignore="true"
          onMouseDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onPointerDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onSelect?.(id);
            onOpenDiscussion(id);
          }}
        >
          <DiscussionIcon size={14} />
          {discussionSummary.unreadCount > 0 && (
            <span className="node-discussion-indicator__count">
              {discussionSummary.unreadCount > 9 ? '9+' : discussionSummary.unreadCount}
            </span>
          )}
        </button>
      )}

      {!semanticZoom && typeof info === 'string' && info.trim() && onOpenInfo && (
        <button
          type="button"
          className={`node-info-indicator${discussionSummary && onOpenDiscussion ? ' node-info-indicator--with-discussion' : ''}`}
          aria-label="Open idea notes"
          title="Open idea notes"
          data-export-ignore="true"
          onMouseDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onPointerDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onSelect?.(id);
            onOpenInfo(id);
          }}
        >
          <NotesIcon size={14} />
        </button>
      )}

      {moderationNotice && (
        <button
          type="button"
          className="node-moderation-indicator"
          aria-label={moderationNotice}
          title={moderationNotice}
          data-export-ignore="true"
          onMouseDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onPointerDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
        >
          !
        </button>
      )}

      {/* Expand/Collapse button */}
      {!semanticZoom && buttonInfo && (buttonInfo?.type === 'collapse'?showCollapseButton:true) && (
        <div
          onClick={handleButtonClick}
          style={{
            position: 'absolute',
            left: buttonInfo.direction === 'right'
              ? 'calc(100% - 8px)' // Position relative to the node's right edge
              : '-8px', // Position relative to the node's left edge
            top: '-8px', // Position relative to the node's top edge
            width: buttonInfo.type === 'expand' ? 'auto' : '16px',
            minWidth: '16px',
            height: '16px',
            padding: buttonInfo.type === 'expand' ? '0 4px' : 0,
            // Use the same background as the node (themed when a theme
            // restyles node fills, e.g. chalk on the chalkboard).
            background: themeNodeStyle?.background || themeColor,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '10px',
            fontWeight: 'bold',
            color: themeNodeStyle?.color || 'white',
            opacity: '0.6',
            boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
            cursor: 'pointer',
            zIndex: 15,
            transition: 'all 0.2s ease',
            border: '1px solid rgba(255,255,255,0.3)' // Subtle border for definition
          }}

          onMouseEnter={(e) => {
            e.target.style.transform = 'scale(1.1)';
            e.target.style.boxShadow = '0 2px 6px rgba(0,0,0,0.4)';
            if (showCollapseButtonTimeout.current) {
              clearTimeout(showCollapseButtonTimeout.current);
            }
            setShowCollapseButton(true);
          }}


          onMouseLeave={(e) => {
            e.target.style.transform = 'scale(1)';
            e.target.style.boxShadow = '0 1px 3px rgba(0,0,0,0.3)';
            showCollapseButtonTimeout.current = setTimeout(() => {
              setShowCollapseButton(false);
            }, 300);
          }}
          title={buttonInfo.tooltip}
        >
          {buttonInfo.type === 'expand' && buttonInfo.count > 0
            ? `+${buttonInfo.count}`
            : buttonInfo.symbol}
        </div>
      )}
    </div>
  );
});

export default Node;
